// Edge-safe "is this session logged in?" check for the proxy auth gate (EWM-482
// Phase 3). It does NOT authenticate — it only inspects the session the request
// already carries.
//
// The `frontastic-session` cookie is a SIGNED JWT (JWS) whose payload is
// base64url-encoded JSON = the Frontastic sessionData. The BFF sets `accountId`
// on it at login (commerce-commercetools AccountController) and clears it at
// logout, and `accountId` presence is the BFF's OWN logged-in gate
// (AccountFetcher.fetchAccountIdFromSessionEnsureLoggedIn). The server side
// already decodes the same payload for customerGroups (session-cache/readers.ts).
//
// COARSE + OPTIMISTIC by design: we DECODE but do NOT verify the signature
// (there's no session secret at the edge). A forged cookie could reach a
// protected *shell*, but the BFF verifies the real session and 401s/empties
// every data action, so a forged session gets NO data. This gate is UX only
// (don't show guests trade chrome; bounce them to login) — never the security
// boundary; the BFF stays the authz.
//
// Edge runtime: uses `atob` (Web API), NOT Node `Buffer`, so it works whether
// the proxy runs in the edge or node runtime.

type SessionPayload = { accountId?: unknown; sessionExpiresAt?: unknown };

// Decode the (unverified) JWT payload. The edge gate trusts the decoded payload
// for UX only — the BFF stays the authz boundary. Uses atob (Web API) because
// this runs in the Next.js edge runtime.
const decodeSessionPayload = (token: string | undefined): SessionPayload | null => {
  if (!token) return null;
  const payload = token.split('.')[1];
  if (!payload) return null;
  try {
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded)) as SessionPayload;
  } catch {
    // Unreadable / malformed token → treat as not-logged-in (the safe default
    // for a protected route: require login rather than let an unknown through).
    return null;
  }
};

export const isLoggedInFromSession = (token: string | undefined): boolean => {
  const decoded = decodeSessionPayload(token);
  return typeof decoded?.accountId === 'string' && decoded.accountId.length > 0;
};

// EWM-535 — true only for an authenticated session whose absolute expiry has
// passed (distinguishes "expired" from "never logged in" so the FE can show a
// session-expired message rather than a bare login prompt).
export const isSessionExpiredFromSession = (token: string | undefined, now: number = Date.now()): boolean => {
  const decoded = decodeSessionPayload(token);
  if (typeof decoded?.accountId !== 'string' || decoded.accountId.length === 0) return false;
  return typeof decoded.sessionExpiresAt === 'number' && decoded.sessionExpiresAt < now;
};
